import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { useScreenPermissions } from '../components/useScreenPermissions';

// --- ICONS ---
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const ProjectViewModal = ({ open, onClose, projectId }) => {
  const [projectDetails, setProjectDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Dropdown Data State (Optional - for displaying names instead of codes)
  const [dropdownData, setDropdownData] = useState({
    customers: [],
    locations: [],
    projectManagers: []
  });

  const { hasPermission, hasFullAccess } = useScreenPermissions();

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      fetchDropdownData();
      
      // Handle ID type: string or object
      const projectNo = typeof projectId === 'string' 
        ? projectId 
        : (projectId?.No || projectId?.No);

      if (projectNo) {
        fetchProjectDetails(projectNo);
      }
    }
  }, [open, projectId]);

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const fetchDropdownData = async () => {
    // If you have a form-data endpoint for projects, use it here
    // try {
    //   const response = await axios.get(`${API_URL}/project/form-data`);
    //   setDropdownData(response.data);
    // } catch (error) {
    //   console.error("Error fetching dropdown data:", error);
    // }
  };

  const fetchProjectDetails = async (projectNo) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/project/${projectNo}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      setProjectDetails(response.data);
    } catch (error) {
      console.error('Error fetching project details:', error);
      setError('Failed to load project details');
      setProjectDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toFixed(2);
  };

  if (!open || !projectId) return null;

  const project = projectDetails || projectId;
  const projectLines = projectDetails?.Items || projectId?.Items || [];

  const isBlocked = project.Blocked === 1 || project.Blocked === true;
  const statusText = isBlocked ? 'Blocked' : 'Active';
  const statusStyle = isBlocked 
    ? 'bg-red-100 text-red-800' 
    : 'bg-green-100 text-green-800';

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {/* Main Modal Container */}
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl'} h-full rounded-none`}>
        
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Project Details
            </h2>
            <div className="flex gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading project details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                Error: {error}
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* Project Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">
                    Project Information
                  </h3>
                  
                  {/* First Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Project No</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase font-semibold">
                        {project.No || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Description</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {project.Description || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Second Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Customer Name</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {project.BillToName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Customer Address</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase text-sm">
                        {[project.BillToAddress, project.BillToAddress2, project.BillToCity].filter(Boolean).join(', ') || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Third Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Project Manager</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {project.ProjectManager || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Person Responsible</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {project.PersonResponsible || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Fourth Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Creation Date</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {formatDate(project.CreationDate)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Currency Code</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {project.CurrencyCode || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Fifth Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                     <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">External Document No</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {project.ExternalDocumentNo || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Status</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusStyle}`}>
                          {statusText}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Project Lines */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">
                      Project Planning Lines
                    </h3>
                  </div>
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <th className="p-2 border border-[#E2E8F0]">#</th>
                          <th className="p-2 border border-[#E2E8F0]">Line No</th>
                          <th className="p-2 border border-[#E2E8F0]">Job Task No</th>
                          <th className="p-2 border border-[#E2E8F0]">Type</th>
                          <th className="p-2 border border-[#E2E8F0]">No</th>
                          <th className="p-2 border border-[#E2E8F0]">Description</th>
                          <th className="p-2 border border-[#E2E8F0]">UOM</th>
                          <th className="p-2 border border-[#E2E8F0]">Quantity</th>
                          <th className="p-2 border border-[#E2E8F0]">Unit Price</th>
                          <th className="p-2 border border-[#E2E8F0]">Total</th>
                          <th className="p-2 border border-[#E2E8F0]">Location</th>
                          <th className="p-2 border border-[#E2E8F0]">Req. Delivery Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projectLines.length > 0 ? (
                          projectLines.map((line, idx) => {
                            const totalLine = (parseFloat(line.Quantity || 0) * parseFloat(line.UnitPrice || 0));
                            return (
                              <tr key={line.ProjectLineId || idx} className="border-t border-[#E2E8F0] hover:bg-gray-50">
                                <td className="p-2 text-center border border-[#E2E8F0]">{idx + 1}</td>
                                <td className="p-2 border border-[#E2E8F0] text-center">{line.LineNo || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] text-center">{line.JobTaskNo || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] text-center uppercase">{line.Type || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] uppercase">{line.ItemNo || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] uppercase">
                                    {/* Description isn't explicitly in the ProjectLine SQL in the prompt, but usually 'No' joins to Items. Using '-' fallback */}
                                    {line.Description || '-'} 
                                </td>
                                <td className="p-2 border border-[#E2E8F0] uppercase">{line.UOM || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] text-right">{line.Quantity || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] text-right">{formatCurrency(line.UnitPrice)}</td>
                                <td className="p-2 border border-[#E2E8F0] text-right font-semibold">{formatCurrency(totalLine)}</td>
                                <td className="p-2 border border-[#E2E8F0] uppercase">{line.LocationCode || '-'}</td>
                                <td className="p-2 border border-[#E2E8F0] text-center">
                                  {line.RequestedDeliveryDate ? dayjs(line.RequestedDeliveryDate).format('YYYY-MM-DD') : '-'}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="12" className="text-center text-gray-500 py-4 border border-[#E2E8F0]">
                              No planning lines found for this project
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Simple Summary Footer */}
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0] flex justify-end">
                    <div className="flex gap-8">
                        <div className="flex flex-col items-end">
                            <span className="text-sm text-gray-500">Total Budget (Est.)</span>
                            <span className="text-xl font-bold text-blue-600">
                                {formatCurrency(projectLines.reduce((sum, line) => sum + (parseFloat(line.Quantity || 0) * parseFloat(line.UnitPrice || 0)), 0))}
                            </span>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectViewModal;